/* Erreichbarkeit: Flutfüllung mit der Spielerhülle (Hülle 1, Clipnodes) von einem Spawn aus; meldet Spawns und Items, die im Festen
 * liegen oder nicht erreichbar sind. Ersetzt das frühere Skript mapgen/reach.js (ADR 0002). Keine DOM-, Datei- oder Node-Zugriffe. */
import type { Vec3 } from '../model/types.ts';
import { CONTENTS, parseEntities, readBspTree } from '../formats/bsp-tree.ts';
import type { BspTree } from '../formats/bsp-tree.ts';
import I18n from '../i18n.ts';

const _ = I18n.t;
export interface ReachProblem { kind: 'solid' | 'unreachable'; classname: string; origin: Vec3 }
export interface ReachResult {
  /** false: keine Startposition (info_player_deathmatch bzw. info_player_start) in der Karte */
  started: boolean;
  /** erreichte Rasterpositionen; truncated: Obergrenze erreicht, das Ergebnis ist dann unvollständig */
  cells: number; truncated: boolean;
  checked: number; problems: ReachProblem[];
}
export interface ReachItem { level: 'ok' | 'info' | 'warn' | 'err'; id: string; text: string }

/** Schrittweite, Stufenhöhe, größter Fall und Zellenobergrenze der Flutfüllung (Faustwerte; Stufen bis 44 wie die Spielerhülle) */
export const REACH = { step: 16, dz: 4, maxUp: 44, maxFall: 400, maxCells: 1500000 } as const;
const CHECKED = /^(info_player_deathmatch|weapon_|ammo_|item_)/;

interface Clip { planes: BspTree['planes']; nodes: { p: number; c: [number, number] }[]; head: number }
function readClip(buf: ArrayBuffer, tree: BspTree): Clip {
  const dv = new DataView(buf), lump = (i: number) => ({ o: dv.getInt32(4 + i * 8, true), l: dv.getInt32(8 + i * 8, true) });
  const C = lump(9), M = lump(14), nodes: Clip['nodes'] = [];
  for (let i = 0; i < C.l / 8; i++) nodes.push({ p: dv.getInt32(C.o + i * 8, true), c: [dv.getInt16(C.o + i * 8 + 4, true), dv.getInt16(C.o + i * 8 + 6, true)] });
  return { planes: tree.planes, nodes, head: dv.getInt32(M.o + 36 + 4, true) };
}

export function analyzeReach(buf: ArrayBuffer): ReachResult {
  const tree = readBspTree(buf), cl = readClip(buf, tree);
  const contents = (x: number, y: number, z: number): number => {
    let n = cl.head;
    while (n >= 0) { const c = cl.nodes[n], pl = cl.planes[c.p]; n = c.c[(pl.n[0] * x + pl.n[1] * y + pl.n[2] * z - pl.d) >= 0 ? 0 : 1]; }
    return n;
  };
  const solid = (x: number, y: number, z: number): boolean => contents(x, y, z) === CONTENTS.SOLID;
  const supported = (x: number, y: number, z: number): boolean => solid(x, y, z - 2) || contents(x, y, z) === CONTENTS.WATER || contents(x, y, z) === CONTENTS.SLIME;
  const valid = (x: number, y: number, z: number): boolean => !solid(x, y, z);
  const ents = parseEntities(tree.entText).map((e) => e.kv);
  const origin = (kv: Record<string, string>): Vec3 | null => { const o = (kv.origin || '').trim().split(/\s+/).map(Number); return o.length === 3 && o.every(Number.isFinite) ? [o[0], o[1], o[2]] : null; };
  const spawns = ents.filter((e) => e.classname === 'info_player_deathmatch'), starts = spawns.length ? spawns : ents.filter((e) => e.classname === 'info_player_start');
  const first = starts.length ? origin(starts[0]) : null;
  const res: ReachResult = { started: !!first, cells: 0, truncated: false, checked: 0, problems: [] };
  if (!first) return res;

  const { step, dz, maxUp, maxFall } = REACH, seen = new Set<string>(), q: Vec3[] = [], key = (x: number, y: number, z: number): string => x + ',' + y + ',' + z;
  /** Fußhöhe unter einem Punkt (fällt bis zum Boden) */
  const settle = (o: Vec3): number => {
    let z0 = o[2];
    for (let k = 0; k < 40; k++) { if (supported(o[0], o[1], z0) || !valid(o[0], o[1], z0 - dz)) return z0; z0 -= dz; }
    return o[2];
  };
  const settled = settle(first);
  q.push([first[0], first[1], settled]); seen.add(key(first[0], first[1], settled));
  // Leitern und Teleporter verbinden die Flutfüllung über ihre Volumen (Untermodelle *N der BSP)
  const bounds = (kv: Record<string, string>): { min: Vec3; max: Vec3 } | null => { const m = /^\*(\d+)$/.exec(kv.model || ''); const md = m ? tree.models[+m[1]] : undefined; return md ? { min: md.min, max: md.max } : null; };
  const ladders = ents.filter((e) => e.classname === 'func_ladder').map(bounds).filter((b): b is { min: Vec3; max: Vec3 } => !!b);
  const portals = ents.filter((e) => e.classname === 'trigger_teleport' && e.target).map((e) => ({ b: bounds(e), to: ents.filter((d) => d.classname === 'info_teleport_destination' && d.targetname === e.target).map(origin).filter((o): o is Vec3 => !!o) })).filter((t) => t.b && t.to.length);
  const push = (x: number, y: number, z: number) => { const k = key(x, y, z); if (!seen.has(k)) { seen.add(k); q.push([x, y, z]); } };
  const dirs = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
  while (q.length) {
    if (seen.size >= REACH.maxCells) { res.truncated = true; break; }
    const [x, y, z] = q.pop() as Vec3; res.cells++;
    for (const l of ladders) {   // an einer Leiter (Nähe der Fläche) hoch und runter
      if (x < l.min[0] - 24 || x > l.max[0] + 24 || y < l.min[1] - 24 || y > l.max[1] + 24 || z < l.min[2] - 16 || z > l.max[2] + 16) continue;
      for (let zz = Math.ceil(l.min[2] / step) * step; zz <= l.max[2]; zz += step) if (valid(x, y, zz)) push(x, y, zz);
    }
    for (const t of portals) {   // Teleporter: Ziel wird erreicht, sobald man das Volumen betritt
      const b = t.b as { min: Vec3; max: Vec3 };
      if (x >= b.min[0] - 16 && x <= b.max[0] + 16 && y >= b.min[1] - 16 && y <= b.max[1] + 16 && z >= b.min[2] - 40 && z <= b.max[2]) t.to.forEach((o) => push(o[0], o[1], settle(o)));
    }
    for (const d of dirs) {
      const nx = x + d[0] * step, ny = y + d[1] * step;
      let found: number | null = null;
      for (let zz = z + maxUp; zz >= z - maxFall; zz -= dz) {
        if (!valid(nx, ny, zz)) { if (zz <= z) break; continue; }
        if (supported(nx, ny, zz)) { found = zz; break; }
        if (!valid(nx, ny, zz - dz)) { let f: number | null = null; for (let k = 1; k < dz; k++) if (supported(nx, ny, zz - k)) { f = zz - k; break; } if (f !== null) { found = f; break; } }   // Feinausrichtung auf Stufenkanten
      }
      if (found === null) continue;
      const mid = Math.max(z, found); if (!valid(x + d[0] * step / 2, y + d[1] * step / 2, mid)) continue;   // Kopffreiheit auf dem Weg
      const k = key(nx, ny, found); if (seen.has(k)) continue;
      seen.add(k); q.push([nx, ny, found]);
    }
  }
  // Zellen nach 32er-Raster ablegen, damit die Prüfung der Entities schnell bleibt
  const idx = new Map<string, Vec3[]>();
  seen.forEach((k) => { const [x, y, z] = k.split(',').map(Number); const kk = (x >> 5) + ',' + (y >> 5); const l = idx.get(kk); if (l) l.push([x, y, z]); else idx.set(kk, [[x, y, z]]); });
  const reachable = (o: Vec3): boolean => {
    const cx = Math.round(o[0] / 32), cy = Math.round(o[1] / 32);
    for (let i = -1; i <= 1; i++) for (let j = -1; j <= 1; j++) {
      const l = idx.get((cx + i) + ',' + (cy + j));
      if (l) for (const p of l) if (Math.abs(p[0] - o[0]) <= 40 && Math.abs(p[1] - o[1]) <= 40 && o[2] - p[2] >= -40 && o[2] - p[2] <= 72) return true;
    }
    return false;
  };
  const hull0Solid = (p: Vec3): boolean => {
    let n = tree.models[0].head;
    while (n >= 0) { const nd = tree.nodes[n], pl = tree.planes[nd.plane]; n = nd.ch[pl.n[0] * p[0] + pl.n[1] * p[1] + pl.n[2] * p[2] - pl.d >= 0 ? 0 : 1]; }
    return tree.leafs[~n].contents === CONTENTS.SOLID;
  };
  ents.forEach((e) => {
    const cn = e.classname || '';
    if (!CHECKED.test(cn)) return;
    const o = origin(e); if (!o) return;
    res.checked++;
    const po: Vec3 = /^info_player/.test(cn) ? o : [o[0], o[1], o[2] + 24];
    if (hull0Solid(o)) { res.problems.push({ kind: 'solid', classname: cn, origin: o }); return; }
    if (!res.truncated && !reachable(po)) res.problems.push({ kind: 'unreachable', classname: cn, origin: o });
  });
  return res;
}

/** Befunde in der Form der Prüfergebnisse (stabile ids reach.*), damit Editor, CLI und Skills dasselbe zeigen */
export function reachItems(r: ReachResult): ReachItem[] {
  if (!r.started) return [{ level: 'info', id: 'reach.no_spawn', text: _('reach.no_spawn') }];
  const out: ReachItem[] = [];
  const names = (k: ReachProblem['kind']): string => r.problems.filter((p) => p.kind === k).slice(0, 4).map((p) => p.classname + ' (' + p.origin.join(' ') + ')').join(', ');
  const solid = r.problems.filter((p) => p.kind === 'solid').length, lost = r.problems.filter((p) => p.kind === 'unreachable').length;
  if (solid) out.push({ level: 'err', id: 'reach.solid', text: _('reach.solid', { count: solid, names: names('solid') }) });
  if (lost) out.push({ level: 'warn', id: 'reach.unreachable', text: _('reach.unreachable', { count: lost, names: names('unreachable') }) });
  if (r.truncated) out.push({ level: 'info', id: 'reach.truncated', text: _('reach.truncated', { cells: r.cells }) });
  if (!out.length) out.push({ level: 'ok', id: 'reach.ok', text: _('reach.ok', { count: r.checked, cells: r.cells }) });
  return out;
}
